import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { contactInfo, services, serviceAreas } from "../mock";
import { locations } from "../locations";
import LegalModal from "./LegalModal";

const locationSlugByName = locations.reduce((acc, l) => {
  acc[l.name] = l.slug;
  return acc;
}, {});

const Footer = () => {
  const [legalDoc, setLegalDoc] = useState(null);

  return (
    <>
      <footer className="bg-slate-900 text-slate-300">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="font-display text-[30px] text-white leading-none" style={{ fontWeight: 900 }}>
                M.A.G
              </div>
              <div className="text-[11px] font-semibold tracking-[0.14em] text-slate-400 mt-1.5">
                M.A.G Facility Services UG
              </div>
              <p className="mt-5 text-[15px] text-slate-400 leading-relaxed max-w-sm">
                Ihr verlässlicher Partner für professionelle Gebäudereinigung in
                Augsburg. Zertifiziert, versichert, transparent.
              </p>
              <ul className="mt-6 space-y-3 text-[14px] text-slate-400">
                <li className="flex items-center gap-2.5">
                  <Phone className="w-4 h-4 text-blue-400" />
                  <a href={contactInfo.phoneHref} className="hover:text-blue-400 transition-colors">{contactInfo.phone}</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <Mail className="w-4 h-4 text-blue-400" />
                  <a href={`mailto:${contactInfo.email}`} className="hover:text-blue-400 transition-colors">{contactInfo.email}</a>
                </li>
                <li className="flex items-center gap-2.5">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span>{contactInfo.address}</span>
                </li>
              </ul>

              <div className="mt-6">
                <h4 className="text-[12px] font-bold tracking-[0.18em] text-slate-500 uppercase mb-3">
                  Einsatzgebiete
                </h4>
                <div className="flex flex-wrap gap-2">
                  {serviceAreas.map((area) => {
                    const slug = locationSlugByName[area];
                    const cls =
                      "text-[12px] text-slate-300 bg-white/5 border border-white/10 rounded-full px-3 py-1";
                    const linkCls = cls + " hover:text-blue-400 hover:border-blue-400/40 transition-colors";
                    if (area === "Augsburg") {
                      return (
                        <Link key={area} to="/" data-testid="footer-location-augsburg" className={linkCls}>
                          {area}
                        </Link>
                      );
                    }
                    return slug ? (
                      <Link
                        key={area}
                        to={`/gebaeudereinigung/${slug}`}
                        data-testid={`footer-location-${slug}`}
                        className={linkCls}
                      >
                        {area}
                      </Link>
                    ) : (
                      <span key={area} className={cls}>
                        {area}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="md:col-span-3">
              <h4 className="text-[12px] font-bold tracking-[0.18em] text-slate-500 uppercase">Leistungen</h4>
              <ul className="mt-4 space-y-2.5">
                {services.map((s) => (
                  <li key={s.id}>
                    <a href={`/leistungen/${s.slug}`} className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-[12px] font-bold tracking-[0.18em] text-slate-500 uppercase">Unternehmen</h4>
              <ul className="mt-4 space-y-2.5">
                <li><a href="/ueber-uns" className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">Über uns</a></li>
                <li><a href="/referenzen" className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">Referenzen</a></li>
                <li><a href="/#kontakt" className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">Kontakt</a></li>
              </ul>
            </div>

            <div className="md:col-span-2">
              <h4 className="text-[12px] font-bold tracking-[0.18em] text-slate-500 uppercase">Rechtliches</h4>
              <ul className="mt-4 space-y-2.5">
                <li><button type="button" onClick={() => setLegalDoc("impressum")} className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">Impressum</button></li>
                <li><button type="button" onClick={() => setLegalDoc("datenschutz")} className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">Datenschutz</button></li>
                <li><button type="button" onClick={() => setLegalDoc("agb")} className="text-[14px] text-slate-400 hover:text-blue-400 transition-colors">AGB</button></li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[13px] text-slate-500">
              © {new Date().getFullYear()} MAG Facility Services UG. Alle Rechte vorbehalten.
            </p>
            <div className="flex gap-5 text-[13px] text-slate-500">
              <button type="button" onClick={() => setLegalDoc("impressum")} className="hover:text-blue-400 transition-colors">Impressum</button>
              <button type="button" onClick={() => setLegalDoc("datenschutz")} className="hover:text-blue-400 transition-colors">Datenschutz</button>
              <button type="button" onClick={() => setLegalDoc("agb")} className="hover:text-blue-400 transition-colors">AGB</button>
            </div>
          </div>
        </div>
      </footer>

      <LegalModal docKey={legalDoc} onClose={() => setLegalDoc(null)} />
    </>
  );
};

export default Footer;
